import type { FrameArtDto, FrameDto } from '@codeframe/api-contract';
import s from './MechSchematic.module.css';

type Slot = FrameDto['slots'][number];

/**
 * Frame art from the repo's `src/frames` and `src/modules`, keyed like the server's layer
 * paths (`frames/X.png`). Converted to WebP at build time; the source PNGs stay untouched.
 */
const ART_URLS: Record<string, string> = Object.fromEntries(
  Object.entries(
    import.meta.glob<string>('../../../../src/{frames,modules}/*.{png,webp}', {
      eager: true,
      import: 'default',
      query: { w: '960', format: 'webp', quality: '82' },
    }),
  ).map(([path, url]) => [path.slice(path.indexOf('/src/') + 5), url]),
);

function ArtSchematic({
  art,
  slots,
  name,
}: {
  art: FrameArtDto;
  slots: readonly Slot[];
  name: string;
}) {
  // Callouts are drawn in art pixels; size them from the canvas height so they read the same
  // whatever resolution the art is.
  const u = art.height / 1000;
  const margin = Math.round(260 * u);
  const layers = art.layers.filter((l) => ART_URLS[l]);
  return (
    <svg
      viewBox={`${-margin} 0 ${art.width + margin * 2} ${art.height}`}
      className={`${s.svg} ${s.art}`}
      role="img"
      aria-label={`${name} schematic`}
    >
      <defs>
        <pattern id="art-grid" width={29 * u} height={29 * u} patternUnits="userSpaceOnUse">
          <path
            d={`M${29 * u} 0H0V${29 * u}`}
            fill="none"
            stroke="rgba(53,208,255,0.06)"
            strokeWidth={1.2 * u}
          />
        </pattern>
      </defs>
      <rect
        x={-margin}
        y="0"
        width={art.width + margin * 2}
        height={art.height}
        fill="url(#art-grid)"
      />
      {layers.map((l) => (
        <image key={l} href={ART_URLS[l]} x="0" y="0" width={art.width} height={art.height} />
      ))}
      {slots.map((slot) => {
        const a = art.anchors[slot.slot];
        if (!a) return null;
        const left = a.side === 'left';
        const edge = left ? -12 * u : art.width + 12 * u;
        const tx = left ? edge - 7 * u : edge + 7 * u;
        const anchor = left ? 'end' : 'start';
        return (
          <g key={slot.slot} className={slot.part ? s.slotFilled : s.slotEmpty}>
            <path
              d={`M${a.x} ${a.y}H${edge}`}
              className={s.leader}
              style={{ strokeWidth: 1.8 * u }}
            />
            <circle
              cx={a.x}
              cy={a.y}
              r={6.6 * u}
              className={s.node}
              style={{ strokeWidth: 2.4 * u }}
            />
            <text
              x={tx}
              y={a.y - 7 * u}
              textAnchor={anchor}
              className={s.slotLabel}
              style={{ fontSize: 18 * u }}
            >
              {slot.slot.split('.')[1]!.toUpperCase()}
            </text>
            <text
              x={tx}
              y={a.y + 24 * u}
              textAnchor={anchor}
              className={s.partLabel}
              style={{ fontSize: 24 * u }}
            >
              {slot.part?.name ?? 'EMPTY'}
            </text>
          </g>
        );
      })}
    </svg>
  );
}

const HOTSPOTS: Record<string, { x: number; y: number; anchor: 'start' | 'end' }> = {
  'head.sensor': { x: 205, y: 58, anchor: 'start' },
  'head.processor': { x: 95, y: 70, anchor: 'end' },
  'torso.reactor': { x: 95, y: 150, anchor: 'end' },
  'torso.armor': { x: 205, y: 170, anchor: 'start' },
  'arms.weapon': { x: 95, y: 220, anchor: 'end' },
  'arms.utility': { x: 205, y: 232, anchor: 'start' },
  'legs.mobility': { x: 205, y: 318, anchor: 'start' },
};

const TARGET: Record<string, { x: number; y: number }> = {
  'head.sensor': { x: 160, y: 52 },
  'head.processor': { x: 150, y: 70 },
  'torso.reactor': { x: 150, y: 150 },
  'torso.armor': { x: 168, y: 176 },
  'arms.weapon': { x: 92, y: 210 },
  'arms.utility': { x: 214, y: 214 },
  'legs.mobility': { x: 170, y: 300 },
};

/**
 * Schematic of the Frame with callouts for each hardware slot: the Frame's layered art when it
 * has some (installed upgrades drawn over the base), otherwise generic line art.
 */
export function MechSchematic({
  slots,
  name,
  art,
}: {
  slots: readonly Slot[];
  name: string;
  art: FrameArtDto | null;
}) {
  if (art && ART_URLS[art.layers[0]!]) return <ArtSchematic art={art} slots={slots} name={name} />;
  return (
    <svg viewBox="-60 0 420 380" className={s.svg} role="img" aria-label={`${name} schematic`}>
      <defs>
        <pattern id="grid" width="16" height="16" patternUnits="userSpaceOnUse">
          <path d="M16 0H0V16" fill="none" stroke="rgba(53,208,255,0.06)" />
        </pattern>
      </defs>
      <rect x="-60" y="0" width="420" height="380" fill="url(#grid)" />
      <g className={s.body}>
        {/* head */}
        <path d="M132 34h36l10 14v22l-10 10h-36l-10-10V48z" />
        <path d="M140 50h22" className={s.visor} />
        {/* torso */}
        <path d="M110 92h80l16 22-8 70-20 22h-56l-20-22-8-70z" />
        <circle cx="150" cy="148" r="14" className={s.core} />
        <path d="M128 196h44" />
        {/* arms */}
        <path d="M100 102l-22 10-14 64 8 44 22 6 8-40 10-58z" />
        <path d="M200 102l22 10 14 64-8 44-22 6-8-40-10-58z" />
        <path d="M70 222l-8 22h22l4-18z" className={s.accent} />
        {/* legs */}
        <path d="M122 210l-10 70 6 60h22l6-60-4-70z" />
        <path d="M178 210l10 70-6 60h-22l-6-60 4-70z" />
        <path d="M108 340h40M152 340h40" />
      </g>
      {slots.map((slot) => {
        const h = HOTSPOTS[slot.slot];
        const t = TARGET[slot.slot];
        if (!h || !t) return null;
        const filled = !!slot.part;
        const lx = h.anchor === 'end' ? h.x - 30 : h.x + 30;
        return (
          <g key={slot.slot} className={filled ? s.slotFilled : s.slotEmpty}>
            <path d={`M${t.x} ${t.y}L${h.x} ${h.y}L${lx} ${h.y}`} className={s.leader} />
            <circle cx={t.x} cy={t.y} r="3.5" className={s.node} />
            <text
              x={lx + (h.anchor === 'end' ? -4 : 4)}
              y={h.y - 4}
              textAnchor={h.anchor}
              className={s.slotLabel}
            >
              {slot.slot.split('.')[1]!.toUpperCase()}
            </text>
            <text
              x={lx + (h.anchor === 'end' ? -4 : 4)}
              y={h.y + 11}
              textAnchor={h.anchor}
              className={s.partLabel}
            >
              {slot.part?.name ?? 'EMPTY'}
            </text>
          </g>
        );
      })}
    </svg>
  );
}
